// BoaterData Signal Forwarder — Client-side fire-and-forget utility
// Routes luxury signals to forward-luxury-signal and general signals to signal-to-boatdata

import { supabase } from '@/integrations/supabase/client';

// Session & anonymous ID helpers (reuse from tracking.ts)
const getSessionId = (): string => {
  const KEY = 'bv_session_id';
  const TS_KEY = 'bv_session_timestamp';
  const TIMEOUT = 30 * 60 * 1000;
  const now = Date.now();
  const last = parseInt(localStorage.getItem(TS_KEY) || '0', 10);
  let id = localStorage.getItem(KEY);
  if (!id || (now - last) > TIMEOUT) {
    id = `sess_${now}_${Math.random().toString(36).substring(2, 9)}`;
    localStorage.setItem(KEY, id);
  }
  localStorage.setItem(TS_KEY, now.toString());
  return id;
};

const getGeoRegion = (): string | null => {
  try {
    const cache = localStorage.getItem('geo_check');
    if (cache) {
      const data = JSON.parse(cache);
      return data.country_code || data.countryCode || null;
    }
  } catch {}
  return null;
};

// Luxury event types handled by forward-luxury-signal edge function
const LUXURY_EVENT_TYPES = new Set([
  'luxury_valuation_submit',
  'superyacht_valuation_submit',
  'intent_escalation',
  'high_value_session',
  'off_market_request',
  'brokerage_inquiry',
  'concierge_request',
  'cash_offer_click',
  'trade_up_use',
  'net_proceeds_use',
  'document_upload',
  'builder_search',
  'spec_download',
]);

// General signal types handled by signal-to-boatdata edge function
const GENERAL_SIGNAL_TYPES = new Set([
  'intent_captured',
  'brokerage_lead_submit',
  'buyer_lead_submit',
  'buyer_valuation_lite_complete',
  'seller_to_buyer_flow_start',
  'contact_info_submit',
  'valuation_step_completed',
  'cta_clicked',
  'finance_prequal_click',
  'insurance_quote_click',
  'buyer_research_start',
  'sign_up',
  'begin_checkout',
  'checkout_completed',
  'tracked_boat_assigned',
  'subscription_canceled',
  'buyer_signal_submit',
]);

interface SignalPayload {
  event_type: string;
  intent_type?: 'buyer' | 'seller' | 'research' | 'finance' | 'insurance';
  entity_context?: {
    brand?: string;
    model?: string;
    year?: number;
    length?: number;
    estimated_value?: number;
  };
  metadata?: Record<string, unknown>;
  // Luxury-specific fields
  yacht_length?: number;
  price_band?: string;
  builder_name?: string;
  engine_type?: string;
  intent_score?: number;
  intent_tier?: string;
  engagement_score?: number;
  valuation_id?: string;
  email?: string;
  phone?: string;
}

/**
 * Fire-and-forget signal to BoaterData.
 * Routes to the correct edge function based on event type.
 * Never blocks the UI — errors are silently logged.
 */
export function signalToBoatData(payload: SignalPayload): void {
  const { event_type } = payload;
  if (!event_type) return;

  // Don't send signals in development
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    console.log('🔔 [BoaterData signal skipped - dev]', event_type, payload);
    return;
  }

  const eventId = crypto.randomUUID();
  const sessionId = getSessionId();
  const geoRegion = getGeoRegion();

  if (LUXURY_EVENT_TYPES.has(event_type)) {
    // Route to forward-luxury-signal (requires auth)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return; // Luxury signals require auth
      supabase.functions.invoke('forward-luxury-signal', {
        body: {
          event_type,
          event_id: eventId,
          email: payload.email,
          phone: payload.phone,
          valuation_id: payload.valuation_id,
          intent_score: payload.intent_score,
          intent_tier: payload.intent_tier,
          yacht_length: payload.yacht_length,
          price_band: payload.price_band,
          builder_name: payload.builder_name,
          engine_type: payload.engine_type,
          geo_region: geoRegion || payload.metadata?.geo_region,
          engagement_score: payload.engagement_score,
          intent_type: payload.intent_type || 'seller',
          metadata: payload.metadata,
        },
      }).catch((err) => console.warn('[BoaterData luxury signal error]', err));
    });
  } else if (GENERAL_SIGNAL_TYPES.has(event_type)) {
    // Route to signal-to-boatdata (works with or without auth)
    supabase.functions.invoke('signal-to-boatdata', {
      body: {
        event_type,
        event_id: eventId,
        session_id: sessionId,
        user_geo: geoRegion,
        intent_type: payload.intent_type || 'research',
        entity_context: payload.entity_context || {},
        metadata: payload.metadata || {},
      },
    }).catch((err) => console.warn('[BoaterData signal error]', err));
  }
}
